import { useOrgAuthStore } from "@solomon-apps/shared";
import {
    createContext,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    EMPTY_LEGAL_ENTITY_TYPE_DRAFT,
    type LegalEntityTypeDraft
} from "@/data/legalEntityTypes";
import type {
    BeneficialOwnershipPayload,
    IncorporationJurisdiction,
    LegalEntityPayload,
    SignatoryAuthorityPayload,
    SourceOfFundsPayload,
    TaxIdentificationPayload,
    UseCaseAndIntentPayload
} from "@/services/clientOnboarding";

import { hydrateLegalEntityFormState } from "./forms/legalEntityFormModel";
import {
    BACKEND_TO_CAPITAL_RANGE,
    BACKEND_TO_DISTRIBUTION,
    BACKEND_TO_USE_CASE,
    INITIAL_USE_CASE_STATE,
    type UseCaseFormState,
    type UseCaseId
} from "./forms/useCaseFormShared";
import { useOnboardingApplicationContextOrNull } from "./OnboardingApplicationContext";

// ─── Legal entity ───────────────────────────────────────────────────────────

export interface LegalEntityDocSlotState {
    documentUuid: string;
    filename: string;
}

export type LegalEntityDocsState = Record<
    | "formation_document"
    | "operating_document"
    | "certificate_of_good_standing",
    LegalEntityDocSlotState | null
>;

/** Who receives legal notices — see Order Form §11. */
/**
 * Who the legal-notice contact mirrors. `null` means "different than
 * existing contacts" — the fields are typed directly. Modelled as null
 * rather than an `"other"` member so the dropdown lists only real
 * people and the empty state renders as the trigger's placeholder.
 */
export type LegalContactSource = "primary" | "signatory";

export interface LegalEntityFormState {
    legalName: string;
    tradingName: string;
    entityType: LegalEntityTypeDraft;
    registeredAddress: string;
    companyNumber: string;
    jurisdiction: IncorporationJurisdiction | null;
    primaryContactName: string;
    primaryContactEmail: string;
    /** Order Form §11: an operational contact does not stand in as the legal
     *  notice contact unless expressly stated. Picking one of the first two
     *  IS that statement, so the common case costs a click instead of a
     *  second set of fields. */
    legalContactSource: LegalContactSource | null;
    legalContactName: string;
    legalContactEmail: string;
    docs: LegalEntityDocsState;
    lastSubmitted: LegalEntityPayload | null;
    taxIdValue: string;
    taxIdJurisdiction: string;
    fatcaClassification: string;
    taxDocUuid: string | null;
    taxDocFilename: string | null;
    lastSubmittedTax: TaxIdentificationPayload | null;
}

export const INITIAL_LEGAL_ENTITY_STATE: LegalEntityFormState = {
    legalName: "",
    tradingName: "",
    entityType: EMPTY_LEGAL_ENTITY_TYPE_DRAFT,
    registeredAddress: "",
    companyNumber: "",
    jurisdiction: null,
    primaryContactName: "",
    primaryContactEmail: "",
    legalContactSource: null,
    legalContactName: "",
    legalContactEmail: "",
    docs: {
        formation_document: null,
        operating_document: null,
        certificate_of_good_standing: null
    },
    lastSubmitted: null,
    taxIdValue: "",
    taxIdJurisdiction: "",
    fatcaClassification: "",
    taxDocUuid: null,
    taxDocFilename: null,
    lastSubmittedTax: null
};

// ─── Signatory ──────────────────────────────────────────────────────────────

/**
 * Where the signatory's identity comes from. The signer is very often
 * someone already named elsewhere in the application — a contact, or a
 * beneficial owner whose photo ID is already on file. Picking them
 * mirrors the fields instead of asking for the same person twice, the
 * same way `LegalContactSource` does for the notice contact.
 *
 * `ubo:<entryUuid>` points at a *submitted* §B.5 entry, so selecting one
 * means an identity document already exists for that person.
 */
export type SignatorySource = "primary" | "legal" | `ubo:${string}`;

export interface SignatoryFormState {
    signatorySource: SignatorySource | null;
    signatoryName: string;
    signatoryEmail: string;
    signatoryTitle: string;
    docUuid: string | null;
    docFilename: string | null;
    lastSubmitted: SignatoryAuthorityPayload | null;
}

export const INITIAL_SIGNATORY_STATE: SignatoryFormState = {
    signatorySource: null,
    signatoryName: "",
    signatoryEmail: "",
    signatoryTitle: "",
    docUuid: null,
    docFilename: null,
    lastSubmitted: null
};

// ─── Source of funds (narrative + supporting slot) ───────────────────────────

export interface FundsNarrativeFormState {
    sourceOfFunds: string;
    jurisdiction: string;
    lastSubmitted: { sourceOfFunds: string; jurisdiction: string } | null;
    supportingDocAttached: boolean;
}

export const INITIAL_FUNDS_NARRATIVE_STATE: FundsNarrativeFormState = {
    sourceOfFunds: "",
    jurisdiction: "",
    lastSubmitted: null,
    supportingDocAttached: false
};

const NARRATIVE_HEADER_SOURCE = "Source of funds:";
const NARRATIVE_HEADER_JURISDICTION = "Jurisdiction of operations:";

function splitNarrative(combined: string): {
    sourceOfFunds: string;
    jurisdiction: string;
} {
    const idx = combined.indexOf(NARRATIVE_HEADER_JURISDICTION);
    if (idx === -1) {
        return { sourceOfFunds: combined.trim(), jurisdiction: "" };
    }
    const sourcePart = combined
        .slice(0, idx)
        .replace(NARRATIVE_HEADER_SOURCE, "")
        .trim();
    const jurisdictionPart = combined
        .slice(idx + NARRATIVE_HEADER_JURISDICTION.length)
        .trim();
    return { sourceOfFunds: sourcePart, jurisdiction: jurisdictionPart };
}

// ─── UBO per-entry ──────────────────────────────────────────────────────────

export interface PersonDetailFormState {
    ownershipPct: string;
    role: string;
    idDocumentUuid: string | null;
    idFilename: string | null;
    lastSubmitted: {
        legalName: string;
        ownershipPct: number;
        idDocumentUuid: string;
        role: string | null;
    } | null;
}

export const INITIAL_PERSON_DETAIL_STATE: PersonDetailFormState = {
    ownershipPct: "",
    role: "",
    idDocumentUuid: null,
    idFilename: null,
    lastSubmitted: null
};

// ─── Wallet draft (label lives on hub `walletEntries`) ──────────────────────

export interface WalletDraftState {
    address: string;
}

// ─── Minting-agreement subsections (§B.3, §B.8–§B.10, §C.2, §D.2) ───────────
//
// These six subsections have no yaas-api step yet — no `STEP_ROUTE_REGISTRY`
// entry, no payload schema, no document purpose. Their payload shapes are
// therefore declared here rather than imported from
// `@/services/clientOnboarding`, and there is no hydration effect for them in
// the provider below: the state is session-local and starts empty on every
// mount. When the BE steps land, swap each local payload interface for the
// service type and add a hydration effect alongside the existing ones.
//
// The same applies to their file slots: with no upload endpoint the forms
// record the chosen file's NAME only (`…Filename`), mirroring the
// `if (!context)` mock branch the live forms already fall back to.

/** Whether the entity has an address for service in the seat of
 *  arbitration. `"no"` is what makes the process-agent appointment apply —
 *  it mirrors the spec slot's `conditional.trigger`. */
export type ArbitralSeatPresence = "yes" | "no";

export interface LegalNoticePayload {
    seatPresence: ArbitralSeatPresence;
    processAgentName: string | null;
    processAgentAddress: string | null;
}

export interface LegalNoticeFormState {
    seatPresence: ArbitralSeatPresence | null;
    processAgentName: string;
    processAgentAddress: string;
    lastSubmitted: LegalNoticePayload | null;
}

export const INITIAL_LEGAL_NOTICE_STATE: LegalNoticeFormState = {
    seatPresence: null,
    processAgentName: "",
    processAgentAddress: "",
    lastSubmitted: null
};

/** What an operator may do with minting access. `"instruct"` submits
 *  instructions only; `"instruct-and-approve"` may also approve them. */
export type OperatorPermission = "instruct" | "instruct-and-approve";

export interface OperatorPayload {
    legalName: string;
    role: string;
    email: string;
    permission: OperatorPermission;
}

export interface OperatorDetailFormState {
    role: string;
    email: string;
    permission: OperatorPermission | null;
    lastSubmitted: OperatorPayload | null;
}

export const INITIAL_OPERATOR_DETAIL_STATE: OperatorDetailFormState = {
    role: "",
    email: "",
    permission: null,
    lastSubmitted: null
};

/** §D.2 Order Form. The MSA panel reads its envelope back from the API on
 *  every mount; the Order Form has no envelope service, so the "sent"
 *  moment is held here instead of in the panel's own `useState` — the
 *  panel unmounts whenever the applicant navigates away. */
export interface OrderFormSigningState {
    /** ISO timestamp of the send action. Null before it's been sent. */
    sentAt: string | null;
}

export const INITIAL_ORDER_FORM_STATE: OrderFormSigningState = {
    sentAt: null
};

const personHydratedKeys = new Set<string>();

// ─── Context ────────────────────────────────────────────────────────────────

type OnboardingFormStateContextValue = {
    useCase: UseCaseFormState;
    setUseCase: Dispatch<SetStateAction<UseCaseFormState>>;
    legalEntity: LegalEntityFormState;
    setLegalEntity: Dispatch<SetStateAction<LegalEntityFormState>>;
    signatory: SignatoryFormState;
    setSignatory: Dispatch<SetStateAction<SignatoryFormState>>;
    fundsNarrative: FundsNarrativeFormState;
    setFundsNarrative: Dispatch<SetStateAction<FundsNarrativeFormState>>;
    personByEntryUuid: Record<string, PersonDetailFormState>;
    setPersonByEntryUuid: Dispatch<
        SetStateAction<Record<string, PersonDetailFormState>>
    >;
    walletDraftBySlotKey: Record<string, WalletDraftState>;
    setWalletDraftBySlotKey: Dispatch<
        SetStateAction<Record<string, WalletDraftState>>
    >;
    legalNotice: LegalNoticeFormState;
    setLegalNotice: Dispatch<SetStateAction<LegalNoticeFormState>>;
    operatorByEntryUuid: Record<string, OperatorDetailFormState>;
    setOperatorByEntryUuid: Dispatch<
        SetStateAction<Record<string, OperatorDetailFormState>>
    >;
    orderForm: OrderFormSigningState;
    setOrderForm: Dispatch<SetStateAction<OrderFormSigningState>>;
};

const OnboardingFormStateContext =
    createContext<OnboardingFormStateContextValue | null>(null);

function useFormStateContext(): OnboardingFormStateContextValue {
    const ctx = useContext(OnboardingFormStateContext);
    if (!ctx) {
        throw new Error(
            "Onboarding form state hooks must render inside OnboardingFormStateProvider"
        );
    }
    return ctx;
}

export function OnboardingFormStateProvider({
    children
}: {
    children: ReactNode;
}): ReactNode {
    const context = useOnboardingApplicationContextOrNull();
    const me = useOrgAuthStore((s) => s.me);
    const orgLegalNameSeed =
        me?.org.legalName?.trim() || me?.org.name?.trim() || null;
    const orgDisplayNameSeed = me?.org.name?.trim() || null;

    const [useCase, setUseCase] = useState<UseCaseFormState>(
        INITIAL_USE_CASE_STATE
    );
    const [legalEntity, setLegalEntity] = useState<LegalEntityFormState>(
        INITIAL_LEGAL_ENTITY_STATE
    );
    const [signatory, setSignatory] = useState<SignatoryFormState>(
        INITIAL_SIGNATORY_STATE
    );
    const [fundsNarrative, setFundsNarrative] =
        useState<FundsNarrativeFormState>(INITIAL_FUNDS_NARRATIVE_STATE);
    const [personByEntryUuid, setPersonByEntryUuid] = useState<
        Record<string, PersonDetailFormState>
    >({});
    const [walletDraftBySlotKey, setWalletDraftBySlotKey] = useState<
        Record<string, WalletDraftState>
    >({});
    // Minting-agreement slices — session-local, no BE hydration (see the
    // block comment above their type declarations).
    const [legalNotice, setLegalNotice] = useState<LegalNoticeFormState>(
        INITIAL_LEGAL_NOTICE_STATE
    );
    const [operatorByEntryUuid, setOperatorByEntryUuid] = useState<
        Record<string, OperatorDetailFormState>
    >({});
    const [orderForm, setOrderForm] = useState<OrderFormSigningState>(
        INITIAL_ORDER_FORM_STATE
    );

    const useCaseHydrated = useRef(false);
    const legalHydrated = useRef(false);
    const taxHydrated = useRef(false);
    const signatoryHydrated = useRef(false);
    const fundsHydrated = useRef(false);

    // Use case — once per hub session
    useEffect(() => {
        if (useCaseHydrated.current) return;
        const payload = context?.stepsByName.use_case_and_intent?.payload as
            | Partial<UseCaseAndIntentPayload>
            | undefined;
        if (!payload?.useCases?.length || !payload.expectedCapitalRange) {
            useCaseHydrated.current = true;
            return;
        }
        const hydratedUseCases = new Set<UseCaseId>();
        for (const value of payload.useCases) {
            const mapped = BACKEND_TO_USE_CASE[value];
            if (mapped) hydratedUseCases.add(mapped);
        }
        const hydratedCapital =
            BACKEND_TO_CAPITAL_RANGE[payload.expectedCapitalRange] ?? null;
        const hydratedDistribution = payload.yieldDistribution
            ? (BACKEND_TO_DISTRIBUTION[payload.yieldDistribution] ?? null)
            : null;
        setUseCase({
            useCases: hydratedUseCases,
            capitalRange: hydratedCapital,
            distribution: hydratedDistribution,
            lastSubmitted: {
                useCases: hydratedUseCases,
                capitalRange: hydratedCapital,
                distribution: hydratedDistribution
            }
        });
        useCaseHydrated.current = true;
    }, [context?.stepsByName.use_case_and_intent]);

    // Company + tax — once per hub session
    useEffect(() => {
        if (legalHydrated.current) return;
        const payload = context?.stepsByName.legal_entity?.payload as
            | Partial<LegalEntityPayload>
            | undefined;
        setLegalEntity((prev) =>
            hydrateLegalEntityFormState({
                initial: prev,
                payload,
                orgLegalNameSeed,
                orgDisplayNameSeed
            })
        );
        legalHydrated.current = true;
    }, [
        context?.stepsByName.legal_entity,
        orgLegalNameSeed,
        orgDisplayNameSeed
    ]);

    useEffect(() => {
        if (taxHydrated.current) return;
        const payload = context?.stepsByName.tax_identification?.payload as
            | Partial<TaxIdentificationPayload>
            | undefined;
        if (!payload) {
            taxHydrated.current = true;
            return;
        }
        setLegalEntity((prev) => {
            const next = { ...prev };
            next.taxIdValue = payload.taxIdValue ?? "";
            next.taxIdJurisdiction = payload.taxIdJurisdiction ?? "";
            next.fatcaClassification = payload.fatcaClassification ?? "";
            if (payload.taxIdDocumentUuid) {
                next.taxDocUuid = payload.taxIdDocumentUuid;
                next.taxDocFilename = "On file";
            }
            if (payload.taxIdValue && payload.taxIdJurisdiction) {
                next.lastSubmittedTax = {
                    taxIdValue: payload.taxIdValue,
                    taxIdJurisdiction: payload.taxIdJurisdiction,
                    taxIdDocumentUuid: payload.taxIdDocumentUuid ?? null,
                    fatcaClassification: payload.fatcaClassification ?? null
                };
            }
            return next;
        });
        taxHydrated.current = true;
    }, [context?.stepsByName.tax_identification]);

    useEffect(() => {
        if (signatoryHydrated.current) return;
        const payload = context?.stepsByName.signatory_authority?.payload as
            | Partial<SignatoryAuthorityPayload>
            | undefined;
        if (!payload) {
            signatoryHydrated.current = true;
            return;
        }
        setSignatory({
            // Hydrated from the server, which stores the resolved person
            // rather than which field it was mirrored from — so the source
            // reads back as a direct entry.
            signatorySource: null,
            signatoryName: payload.signatoryName ?? "",
            signatoryEmail: payload.signatoryEmail ?? "",
            signatoryTitle: payload.signatoryTitle ?? "",
            docUuid: payload.authorizationDocumentUuid ?? null,
            docFilename: payload.authorizationDocumentUuid ? "On file" : null,
            lastSubmitted:
                payload.signatoryName &&
                payload.signatoryEmail &&
                payload.signatoryTitle
                    ? {
                          signatoryName: payload.signatoryName,
                          signatoryEmail: payload.signatoryEmail,
                          signatoryTitle: payload.signatoryTitle,
                          authorizationDocumentUuid:
                              payload.authorizationDocumentUuid ?? null
                      }
                    : null
        });
        signatoryHydrated.current = true;
    }, [context?.stepsByName.signatory_authority]);

    useEffect(() => {
        if (fundsHydrated.current) return;
        const payload = context?.stepsByName.source_of_funds?.payload as
            | Partial<SourceOfFundsPayload>
            | undefined;
        if (!payload) {
            fundsHydrated.current = true;
            return;
        }
        if (payload.sourceDescription) {
            const split = splitNarrative(payload.sourceDescription);
            setFundsNarrative({
                sourceOfFunds: split.sourceOfFunds,
                jurisdiction: split.jurisdiction,
                lastSubmitted: {
                    sourceOfFunds: split.sourceOfFunds,
                    jurisdiction: split.jurisdiction
                },
                supportingDocAttached: Boolean(payload.supportingDocumentUuid)
            });
        } else if (payload.supportingDocumentUuid) {
            setFundsNarrative((prev) => ({
                ...prev,
                supportingDocAttached: true
            }));
        }
        fundsHydrated.current = true;
    }, [context?.stepsByName.source_of_funds]);

    const value = useMemo(
        () => ({
            useCase,
            setUseCase,
            legalEntity,
            setLegalEntity,
            signatory,
            setSignatory,
            fundsNarrative,
            setFundsNarrative,
            personByEntryUuid,
            setPersonByEntryUuid,
            walletDraftBySlotKey,
            setWalletDraftBySlotKey,
            legalNotice,
            setLegalNotice,
            operatorByEntryUuid,
            setOperatorByEntryUuid,
            orderForm,
            setOrderForm
        }),
        [
            useCase,
            legalEntity,
            signatory,
            fundsNarrative,
            personByEntryUuid,
            walletDraftBySlotKey,
            legalNotice,
            operatorByEntryUuid,
            orderForm
        ]
    );

    return (
        <OnboardingFormStateContext.Provider value={value}>
            {children}
        </OnboardingFormStateContext.Provider>
    );
}

export function useUseCaseFormState(): [
    UseCaseFormState,
    Dispatch<SetStateAction<UseCaseFormState>>
] {
    const { useCase, setUseCase } = useFormStateContext();
    return [useCase, setUseCase];
}

export function useLegalEntityFormState(): [
    LegalEntityFormState,
    Dispatch<SetStateAction<LegalEntityFormState>>
] {
    const { legalEntity, setLegalEntity } = useFormStateContext();
    return [legalEntity, setLegalEntity];
}

/**
 * Every §B.5 beneficiary that has been saved — name plus the entry uuid.
 * Only submitted entries appear, because an unsaved one has no verified
 * identity document behind it and so cannot stand in for the signatory.
 */
export function useSubmittedBeneficiaries(): Array<{
    entryUuid: string;
    legalName: string;
}> {
    const { personByEntryUuid } = useFormStateContext();
    return useMemo(
        () =>
            Object.entries(personByEntryUuid).flatMap(([entryUuid, person]) =>
                person.lastSubmitted
                    ? [{ entryUuid, legalName: person.lastSubmitted.legalName }]
                    : []
            ),
        [personByEntryUuid]
    );
}

export function useSignatoryFormState(): [
    SignatoryFormState,
    Dispatch<SetStateAction<SignatoryFormState>>
] {
    const { signatory, setSignatory } = useFormStateContext();
    return [signatory, setSignatory];
}

export function useFundsNarrativeFormState(): [
    FundsNarrativeFormState,
    Dispatch<SetStateAction<FundsNarrativeFormState>>
] {
    const { fundsNarrative, setFundsNarrative } = useFormStateContext();
    return [fundsNarrative, setFundsNarrative];
}

export function usePersonDetailFormState(
    entryUuid: string | undefined
): [
    PersonDetailFormState,
    (patch: SetStateAction<PersonDetailFormState>) => void
] {
    const context = useOnboardingApplicationContextOrNull();
    const { personByEntryUuid, setPersonByEntryUuid } = useFormStateContext();

    useEffect(() => {
        if (!entryUuid || personHydratedKeys.has(entryUuid)) return;
        const payload = context?.stepsByName.beneficial_ownership?.payload as
            | Partial<BeneficialOwnershipPayload>
            | undefined;
        const existing = payload?.entries?.find(
            (e) => e.entryUuid === entryUuid
        );
        if (existing) {
            setPersonByEntryUuid((prev) => {
                if (prev[entryUuid]) return prev;
                const next: PersonDetailFormState = {
                    ...INITIAL_PERSON_DETAIL_STATE
                };
                if (typeof existing.ownershipPercent === "number") {
                    next.ownershipPct = String(existing.ownershipPercent);
                }
                if (existing.governmentIdUuid) {
                    next.idDocumentUuid = existing.governmentIdUuid;
                    next.idFilename = "On file";
                }
                if (typeof existing.role === "string") {
                    next.role = existing.role;
                }
                if (
                    typeof existing.legalName === "string" &&
                    typeof existing.ownershipPercent === "number" &&
                    existing.governmentIdUuid
                ) {
                    next.lastSubmitted = {
                        legalName: existing.legalName,
                        ownershipPct: existing.ownershipPercent,
                        idDocumentUuid: existing.governmentIdUuid,
                        role: existing.role ?? null
                    };
                }
                return { ...prev, [entryUuid]: next };
            });
        }
        personHydratedKeys.add(entryUuid);
    }, [
        entryUuid,
        context?.stepsByName.beneficial_ownership,
        setPersonByEntryUuid
    ]);

    const state =
        (entryUuid ? personByEntryUuid[entryUuid] : undefined) ??
        INITIAL_PERSON_DETAIL_STATE;

    const setState = useCallback(
        (patch: SetStateAction<PersonDetailFormState>) => {
            if (!entryUuid) return;
            setPersonByEntryUuid((prev) => {
                const current = prev[entryUuid] ?? INITIAL_PERSON_DETAIL_STATE;
                const next =
                    typeof patch === "function" ? patch(current) : patch;
                return { ...prev, [entryUuid]: next };
            });
        },
        [entryUuid, setPersonByEntryUuid]
    );

    return [state, setState];
}

export function useLegalNoticeFormState(): [
    LegalNoticeFormState,
    Dispatch<SetStateAction<LegalNoticeFormState>>
] {
    const { legalNotice, setLegalNotice } = useFormStateContext();
    return [legalNotice, setLegalNotice];
}

export function useOrderFormSigningState(): [
    OrderFormSigningState,
    Dispatch<SetStateAction<OrderFormSigningState>>
] {
    const { orderForm, setOrderForm } = useFormStateContext();
    return [orderForm, setOrderForm];
}

/**
 * Per-operator slice, keyed by the hub-generated `entryUuid` — the same
 * shape `usePersonDetailFormState` uses for UBOs, minus the hydration
 * effect (§B.9 has no persisted step to hydrate from).
 */
export function useOperatorDetailFormState(
    entryUuid: string | undefined
): [
    OperatorDetailFormState,
    (patch: SetStateAction<OperatorDetailFormState>) => void
] {
    const { operatorByEntryUuid, setOperatorByEntryUuid } =
        useFormStateContext();

    const state =
        (entryUuid ? operatorByEntryUuid[entryUuid] : undefined) ??
        INITIAL_OPERATOR_DETAIL_STATE;

    const setState = useCallback(
        (patch: SetStateAction<OperatorDetailFormState>) => {
            if (!entryUuid) return;
            setOperatorByEntryUuid((prev) => {
                const current =
                    prev[entryUuid] ?? INITIAL_OPERATOR_DETAIL_STATE;
                const next =
                    typeof patch === "function" ? patch(current) : patch;
                return { ...prev, [entryUuid]: next };
            });
        },
        [entryUuid, setOperatorByEntryUuid]
    );

    return [state, setState];
}

/** `slotKey` is stable for the row (e.g. `wallet-0`). */
export function useWalletDraftState(
    slotKey: string
): [WalletDraftState, Dispatch<SetStateAction<WalletDraftState>>] {
    const { walletDraftBySlotKey, setWalletDraftBySlotKey } =
        useFormStateContext();
    const state = walletDraftBySlotKey[slotKey] ?? { address: "" };
    const setState = useCallback(
        (patch: SetStateAction<WalletDraftState>) => {
            setWalletDraftBySlotKey((prev) => {
                const current = prev[slotKey] ?? { address: "" };
                const next =
                    typeof patch === "function" ? patch(current) : patch;
                return { ...prev, [slotKey]: next };
            });
        },
        [slotKey, setWalletDraftBySlotKey]
    );
    return [state, setState];
}
